import { useEffect, useId, useRef, type ReactNode } from "react";
import { Icon } from "@/components/icon/Icon";

/**
 * 네이티브 <dialog> 래퍼. showModal() 하나로 aria-modal·ESC 닫기·focus trap·바깥 요소 inert·
 * 닫힐 때 트리거로 포커스 복원까지 브라우저가 처리해요. 배경 스크롤 락과 ::backdrop 색은
 * globals.css 에 있어요.
 */

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** 다이얼로그 제목 — aria-labelledby 로 연결돼요. */
  title: string;
  children: ReactNode;
}

export function Dialog({ open, onClose, title, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open) {
      // showModal 이 없는 구형 브라우저(Safari < 15.4)에서도 내용은 보이도록 비모달로 폴백해요.
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    } else if (dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: 배경 클릭 닫기는 마우스 전용 편의 기능이에요 — ESC(cancel)와 닫기 버튼이 키보드 접근을 이미 보장해요.
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      // ESC(cancel → close)와 close() 호출 모두 close 이벤트로 들어와요. 닫기 버튼/배경 클릭도
      // ref.current?.close() 를 거쳐 여기로 모여요 — onClose 가 중복 호출되지 않도록 닫기 경로를
      // 하나로 통일해요.
      onClose={onClose}
      // 배경 클릭: 클릭 타깃이 <dialog> 자신일 때만 닫아요. 패딩을 안쪽 wrapper 로 옮겨서, 카드
      // 내부(패딩 포함) 클릭은 e.target 이 wrapper/children 이 되어 여기로 오지 않아요.
      onClick={(e) => {
        if (e.target === ref.current) ref.current?.close();
      }}
      className="m-auto w-[min(48rem,calc(100vw-2rem))] overflow-hidden rounded-lg bg-background-elevated text-label-normal shadow-normal"
    >
      <div className="max-h-[85vh] overflow-y-auto p-6 sm:p-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <h2 id={titleId} className="wds-title-2 text-label-strong">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => ref.current?.close()}
            aria-label="닫기"
            className="-mr-2 -mt-1 shrink-0 rounded-md p-2 text-label-assistive transition-colors duration-200 ease-standard hover:text-label-strong"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

export default Dialog;
